import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { ChevronUp, ChevronDown, Loader2, Check, X, Pencil } from "lucide-react";

interface EvaluationTrack {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
}

const TRACKS_QUERY_KEY = ["/api/admin/evaluation-tracks"];

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export function AnimalEvaluationTracksSettingsCard() {
  const { toast } = useToast();

  const { data: tracks = [], isLoading } = useQuery<EvaluationTrack[]>({
    queryKey: TRACKS_QUERY_KEY,
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: object }) =>
      apiRequest("PATCH", `/api/admin/evaluation-tracks/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRACKS_QUERY_KEY });
    },
    onError: (_err, _vars, _ctx) => {
      toast({ title: "Error", description: "Could not update track.", variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: TRACKS_QUERY_KEY });
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: object) =>
      apiRequest("POST", "/api/admin/evaluation-tracks", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRACKS_QUERY_KEY });
      setNewName("");
      setNewSlug("");
      setNewDescription("");
      toast({ title: "Track added", description: "Evaluation track created." });
    },
    onError: () => {
      toast({ title: "Error", description: "Could not create track.", variant: "destructive" });
    },
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newDescription, setNewDescription] = useState("");

  const sortedTracks = [...tracks].sort((a, b) => a.sortOrder - b.sortOrder);
  const activeTracks = sortedTracks.filter((t) => t.isActive);
  const inactiveTracks = sortedTracks.filter((t) => !t.isActive);

  function startEdit(track: EvaluationTrack) {
    setEditingId(track.id);
    setEditingName(track.name);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingName("");
  }

  function commitEdit(id: string) {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    patchMutation.mutate({ id, payload: { name: trimmed } });
    setEditingId(null);
    setEditingName("");
  }

  function handleReorder(id: string, direction: "up" | "down") {
    const ids = activeTracks.map((t) => t.id);
    const idx = ids.indexOf(id);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= ids.length) return;

    const a = activeTracks[idx];
    const b = activeTracks[swapIdx];

    patchMutation.mutate({ id: a.id, payload: { sortOrder: b.sortOrder } });
    patchMutation.mutate({ id: b.id, payload: { sortOrder: a.sortOrder } });
  }

  function handleAdd() {
    const name = newName.trim();
    const slug = newSlug.trim();
    if (!name) {
      toast({ title: "Validation error", description: "Name is required.", variant: "destructive" });
      return;
    }
    if (!slug) {
      toast({ title: "Validation error", description: "Slug is required.", variant: "destructive" });
      return;
    }
    if (!/^[a-z0-9-]+$/.test(slug)) {
      toast({ title: "Validation error", description: "Slug must contain only lowercase letters, numbers, and hyphens.", variant: "destructive" });
      return;
    }
    createMutation.mutate({
      name,
      slug,
      description: newDescription.trim() || null,
    });
  }

  if (isLoading) {
    return (
      <Card data-testid="card-evaluation-tracks-settings">
        <CardHeader>
          <CardTitle>Evaluation Tracks</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-evaluation-tracks-settings">
      <CardHeader>
        <CardTitle>Evaluation Tracks</CardTitle>
        <CardDescription>
          Evaluation tracks group conditional checklist items by animal intake type.
          Each animal is assigned one track. Core evaluation items appear on all tracks.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {activeTracks.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="text-no-tracks">
            No active evaluation tracks.
          </p>
        )}

        <div className="divide-y">
          {activeTracks.map((track, idx) => (
            <div
              key={track.id}
              className="flex items-center gap-3 py-3"
              data-testid={`row-track-${track.id}`}
            >
              <div className="flex flex-col gap-0.5">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleReorder(track.id, "up")}
                  disabled={idx === 0 || patchMutation.isPending || track.isDefault}
                  data-testid={`button-track-move-up-${track.id}`}
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleReorder(track.id, "down")}
                  disabled={idx === activeTracks.length - 1 || patchMutation.isPending || track.isDefault}
                  data-testid={`button-track-move-down-${track.id}`}
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="flex-1 min-w-0">
                {editingId === track.id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitEdit(track.id);
                        if (e.key === "Escape") cancelEdit();
                      }}
                      className="h-8 text-sm"
                      autoFocus
                      placeholder="Track name"
                      data-testid={`input-edit-track-name-${track.id}`}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => commitEdit(track.id)}
                      disabled={patchMutation.isPending}
                      data-testid={`button-save-track-name-${track.id}`}
                    >
                      {patchMutation.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={cancelEdit}
                      data-testid={`button-cancel-track-edit-${track.id}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate" data-testid={`text-track-name-${track.id}`}>
                      {track.name}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono" data-testid={`text-track-slug-${track.id}`}>
                      {track.slug}
                    </span>
                    {track.isDefault && (
                      <Badge variant="secondary" className="text-xs shrink-0" data-testid={`badge-track-default-${track.id}`}>
                        Default
                      </Badge>
                    )}
                    {!track.isDefault && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startEdit(track)}
                        data-testid={`button-edit-track-name-${track.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                {track.isDefault ? (
                  <span className="text-xs text-muted-foreground" data-testid={`text-track-default-note-${track.id}`}>
                    Cannot deactivate
                  </span>
                ) : (
                  <>
                    <Switch
                      checked={track.isActive}
                      onCheckedChange={(checked) =>
                        patchMutation.mutate({ id: track.id, payload: { isActive: checked } })
                      }
                      disabled={patchMutation.isPending}
                      data-testid={`switch-track-active-${track.id}`}
                    />
                    <span className="text-xs text-muted-foreground w-14">
                      {track.isActive ? "Active" : "Inactive"}
                    </span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {inactiveTracks.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Inactive Tracks
            </p>
            {inactiveTracks.map((track) => (
              <div
                key={track.id}
                className="flex items-center justify-between gap-3 py-2 opacity-60"
                data-testid={`row-track-${track.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm line-through truncate" data-testid={`text-track-name-${track.id}`}>
                    {track.name}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">{track.slug}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Switch
                    checked={false}
                    onCheckedChange={(checked) =>
                      patchMutation.mutate({ id: track.id, payload: { isActive: checked } })
                    }
                    disabled={patchMutation.isPending}
                    data-testid={`switch-track-active-${track.id}`}
                  />
                  <span className="text-xs text-muted-foreground">Inactive</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <Separator />

        <div className="space-y-3">
          <p className="text-sm font-medium">Add evaluation track</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="new-track-name" className="text-xs text-muted-foreground">
                Name
              </Label>
              <Input
                id="new-track-name"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  setNewSlug(toSlug(e.target.value));
                }}
                placeholder="e.g. Owner Surrender"
                data-testid="input-new-track-name"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-track-slug" className="text-xs text-muted-foreground">
                Slug (auto-generated, editable)
              </Label>
              <Input
                id="new-track-slug"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="e.g. owner-surrender"
                className="font-mono text-sm"
                data-testid="input-new-track-slug"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="new-track-description" className="text-xs text-muted-foreground">
                Description (optional)
              </Label>
              <Textarea
                id="new-track-description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Brief description of when this track applies..."
                className="resize-none text-sm"
                rows={2}
                data-testid="textarea-new-track-description"
              />
            </div>
          </div>
          <Button
            onClick={handleAdd}
            disabled={createMutation.isPending || !newName.trim() || !newSlug.trim()}
            data-testid="button-add-track"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Add track
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
